import { useEffect, useCallback } from 'react';
import { imageCache } from '../utils/imageCache';

/**
 * Hook for managing image caching in components
 * @param {Array<string>} imageUrls - Array of image URLs to cache
 * @param {Object} options - Configuration options
 * @returns {Object} - Cache utilities
 */
export const useImageCache = (imageUrls = [], options = {}) => {
  const { 
    preloadOnMount = true,
    logProgress = false 
  } = options;

  // Preload images when component mounts or imageUrls change
  useEffect(() => {
    if (preloadOnMount && imageUrls.length > 0) {
      const validUrls = imageUrls.filter(url => url && typeof url === 'string');
      
      if (validUrls.length > 0) {
        if (logProgress) {
          console.log(`🖼️ Preloading ${validUrls.length} images...`);
        }
        
        imageCache.preloadImages(validUrls).then((results) => {
          const successful = results.filter(result => result.status === 'fulfilled').length;
          const failed = results.filter(result => result.status === 'rejected').length;
          
          if (logProgress) {
            console.log(`✅ Image preloading complete: ${successful} successful, ${failed} failed`);
          }
        });
      }
    }
  }, [imageUrls, preloadOnMount, logProgress]);

  // Get cached URL for a single image
  const getCachedUrl = useCallback((imageUrl) => {
    return imageCache.getCachedUrl(imageUrl);
  }, []);

  // Preload a single image manually
  const preloadImage = useCallback((imageUrl) => {
    return imageCache.preloadImage(imageUrl);
  }, []);

  // Check if image is cached
  const isCached = useCallback((imageUrl) => {
    return imageCache.isCached(imageUrl);
  }, []);

  // Get cache statistics
  const getCacheStats = useCallback(() => {
    return {
      size: imageCache.getCacheSize(),
      cached: imageUrls.filter(url => imageCache.isCached(url)).length,
      total: imageUrls.length
    };
  }, [imageUrls]);

  return {
    getCachedUrl,
    preloadImage,
    isCached,
    getCacheStats,
    clearCache: imageCache.clearCache.bind(imageCache)
  };
};

export default useImageCache;
